import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Box, Typography } from "@mui/material";

import { ds } from "../../theme/ds";
import type { WidgetProps } from "../types";

// The design system's `.callout`, three severities only (info / warning /
// danger; never a green success callout): tinted panel, 1px semantic border,
// radius 8, 20px icon, 13px bold title in the semantic colour, 13px gray-600
// body, optional "Action:" line. Used on the Opportunity page for anything that
// would otherwise be a paragraph of letter text (Bill, 2026-09-08).
const ICONS = {
    info: InfoOutlinedIcon,
    warning: WarningAmberOutlinedIcon,
    danger: ErrorOutlineIcon,
} as const;

export function Callout({ widget }: WidgetProps<"callout">) {
    return <CalloutBox {...widget.props} />;
}

export function CalloutBox({
    variant,
    title,
    body,
    action,
}: {
    variant: "info" | "warning" | "danger";
    title?: string;
    body: string;
    action?: string;
}) {
    const tone = ds[variant];
    const Icon = ICONS[variant];

    return (
        <Box
            sx={{
                display: "flex",
                gap: "12px",
                alignItems: "flex-start",
                p: "16px 20px",
                borderRadius: `${ds.radius}px`,
                border: `1px solid ${tone.border}`,
                bgcolor: tone.bg,
            }}
        >
            <Icon sx={{ width: 20, height: 20, mt: "1px", color: tone.text, flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {title && (
                    <Typography
                        component="div"
                        sx={{ fontWeight: 700, fontSize: ds.text.sm, color: tone.text, mb: "4px" }}
                    >
                        {title}
                    </Typography>
                )}
                <Typography component="div" sx={{ fontSize: ds.text.sm, color: ds.gray600 }}>
                    {body}
                </Typography>
                {action && (
                    <Typography
                        component="div"
                        sx={{ mt: "10px", fontSize: ds.text.sm, color: ds.gray600 }}
                    >
                        <Box component="span" sx={{ fontWeight: 700, color: ds.navy }}>
                            Action:{" "}
                        </Box>
                        {action}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}
